import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Check, ExternalLink, MessagesSquare } from 'lucide-react';
import InquiryModal from '../../components/features/InquiryModal';
import { getPartnersPage } from '../../api/partners';

/*
 * 파트너 — 현장을 같이 완성하는 곳들.
 *
 * 문구와 파트너는 전부 API 에서 온다(`{ page, sections }`). 네이버용 SSR 이 같은 응답으로
 * 본문을 심기 때문에, 여기에 문구를 따로 적으면 사람과 수집기가 보는 글이 갈라진다.
 * 문구를 고치려면 server/src/modules/cms/partners.service.ts 의 PARTNERS_PAGE 를 고친다.
 *
 * 시스템 연동이 맨 앞이고 핵심 파트너는 크게 보여 준다 — 고객의 첫 질문이 "지금 쓰는
 * 시스템(ERP·그룹웨어·POS)과 연동되나요?" 이기 때문이다. 고객은 목록에 넣지 않고 맨 끝
 * 문장으로 말한다.
 *
 * 테두리 있는 <button> 에는 `border-solid` 를 붙인다(전역 `button { border: none }`, Home 참고).
 */

const Logo = ({ partner, className }) => (
    <div className={`flex items-center justify-center rounded-2xl bg-white border border-gray-200 ${className}`}>
        {partner.logoUrl ? (
            <img
                src={partner.logoUrl}
                alt={`${partner.name} 로고`}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
            />
        ) : (
            // 로고를 아직 못 받았으면 이름으로 대신한다. 빈 칸보다 낫다.
            <span className="text-lg font-extrabold text-gray-800 tracking-tight text-center break-keep">
                {partner.altName || partner.name}
            </span>
        )}
    </div>
);

const Highlights = ({ items, columns = false }) =>
    items?.length > 0 && (
        <ul className={`mt-5 list-none p-0 m-0 grid gap-x-6 gap-y-2.5 ${columns ? 'md:grid-cols-2' : ''}`}>
            {items.map((h) => (
                <li key={h} className="flex items-start gap-2 text-[15px]/relaxed text-gray-700 break-keep">
                    <Check className="w-4 h-4 mt-1 text-primary-500 shrink-0" strokeWidth={3} aria-hidden="true" />
                    {h}
                </li>
            ))}
        </ul>
    );

const PRIMARY_LINK = 'inline-flex items-center gap-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-5 py-2.5 transition-colors';
const SECONDARY_LINK = 'inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-300 hover:border-primary-500 text-gray-700 hover:text-primary-700 text-sm font-bold px-5 py-2.5 transition-colors';

/** 사이트 안 링크 중 첫 번째만 분홍 — 버튼 둘이 같은 색이면 어디를 누를지 흐려진다. */
const Links = ({ partner }) =>
    (partner.solutionPath || partner.linkPath || partner.websiteUrl) && (
        <div className="mt-6 flex flex-wrap gap-2.5">
            {partner.solutionPath && (
                <Link to={partner.solutionPath} className={PRIMARY_LINK}>
                    연동 솔루션 보기
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                </Link>
            )}
            {partner.linkPath && (
                <Link to={partner.linkPath} className={partner.solutionPath ? SECONDARY_LINK : PRIMARY_LINK}>
                    {partner.linkLabel || '자세히 보기'}
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                </Link>
            )}
            {partner.websiteUrl && (
                <a
                    href={partner.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={SECONDARY_LINK}
                >
                    홈페이지
                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden="true" />
                    <span className="sr-only">(새 창)</span>
                </a>
            )}
        </div>
    );

const Name = ({ partner, className }) => (
    <h3 className={`m-0 font-extrabold text-gray-900 tracking-tight break-keep ${className}`}>
        {partner.name}
        {partner.altName && partner.altName !== partner.name && (
            <span className="ml-2 text-sm font-bold text-gray-400 tracking-normal">{partner.altName}</span>
        )}
    </h3>
);

/** 핵심 파트너 — 한 줄을 다 쓰고, 소개와 특징을 모두 보여 준다. */
const KeyPartnerCard = ({ partner }) => (
    <article
        id={partner.slug}
        className="scroll-mt-28 rounded-3xl border-2 border-primary-200 bg-white shadow-soft p-6 md:p-10 grid md:grid-cols-[240px_1fr] gap-6 md:gap-10"
    >
        <Logo partner={partner} className="h-28 md:h-36 p-6" />
        <div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 text-white text-xs font-extrabold px-3 py-1">
                    <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden="true" />
                    핵심 파트너
                </span>
                {/* 배지에 '핵심파트너' 를 적으면 바로 앞 표시와 겹친다 — 같은 말은 한 번만. */}
                {partner.badge && partner.badge.replace(/\s/g, '') !== '핵심파트너' && (
                    <span className="rounded-full bg-primary-50 text-primary-700 text-xs font-bold px-3 py-1">{partner.badge}</span>
                )}
            </div>
            <Name partner={partner} className="mt-3 text-2xl md:text-3xl" />
            <p className="mt-2 text-lg/relaxed font-bold text-gray-800 break-keep text-balance">{partner.tagline}</p>
            {partner.summary && (
                <p className="mt-3 text-base/relaxed text-gray-600 break-keep whitespace-pre-line">{partner.summary}</p>
            )}
            <Highlights items={partner.highlights} columns />
            <Links partner={partner} />
        </div>
    </article>
);

const PartnerCard = ({ partner }) => (
    <article id={partner.slug} className="scroll-mt-28 rounded-3xl border border-gray-200 bg-white p-6 md:p-8 flex flex-col">
        <div className="flex items-start justify-between gap-4">
            <Logo partner={partner} className="h-16 w-40 p-3 shrink-0" />
            {partner.badge && (
                <span className="rounded-full bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1 text-right break-keep">{partner.badge}</span>
            )}
        </div>
        <Name partner={partner} className="mt-5 text-xl md:text-2xl" />
        <p className="mt-2 text-base/relaxed font-bold text-gray-800 break-keep text-balance">{partner.tagline}</p>
        {partner.summary && (
            <p className="mt-2 text-[15px]/relaxed text-gray-600 break-keep whitespace-pre-line">{partner.summary}</p>
        )}
        <Highlights items={partner.highlights} />
        {/* 버튼은 카드 바닥에 붙인다 — 옆 카드와 높이가 달라도 버튼 줄이 맞는다. */}
        <div className="mt-auto">
            <Links partner={partner} />
        </div>
    </article>
);

const Skeleton = () => (
    <div className="max-w-5xl mx-auto px-6 pt-36 md:pt-40 pb-24 animate-pulse" aria-hidden="true">
        <div className="h-4 w-16 bg-gray-200 rounded mx-auto" />
        <div className="mt-5 h-12 w-2/3 bg-gray-200 rounded-lg mx-auto" />
        <div className="mt-5 h-5 w-3/4 bg-gray-200 rounded mx-auto" />
        <div className="mt-16 h-64 bg-white rounded-3xl" />
        <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="h-56 bg-white rounded-3xl" />
            <div className="h-56 bg-white rounded-3xl" />
        </div>
    </div>
);

const Partners = () => {
    const location = useLocation();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [modal, setModal] = useState({ isOpen: false, type: 'CONTACT' });

    const load = useCallback(async () => {
        setError('');
        try {
            setData(await getPartnersPage());
        } catch {
            setError('파트너 정보를 불러오지 못했습니다.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // `/partners#allthatpay` 처럼 들어오면, 카드가 그려진 뒤에 그 자리로 옮긴다.
    useEffect(() => {
        if (!data || !location.hash) return undefined;
        const t = setTimeout(() => {
            document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView({ block: 'start' });
        }, 60);
        return () => clearTimeout(t);
    }, [data, location.hash]);

    const openContact = () => setModal({ isOpen: true, type: 'CONTACT' });

    if (error) {
        return (
            <div className="bg-surface-bg min-h-screen pt-40 px-6 text-center">
                <p className="text-gray-600">{error}</p>
                <button
                    type="button"
                    onClick={load}
                    className="mt-4 rounded-full bg-white border border-solid border-gray-300 px-5 py-2 text-sm font-bold text-gray-700 hover:border-primary-500"
                >
                    다시 불러오기
                </button>
            </div>
        );
    }
    if (!data) return <div className="bg-surface-bg min-h-screen"><Skeleton /></div>;

    const { page, sections } = data;

    return (
        <div className="bg-surface-bg min-h-screen pb-24">
            <InquiryModal
                isOpen={modal.isOpen}
                onClose={() => setModal((prev) => ({ ...prev, isOpen: false }))}
                type={modal.type}
            />

            <section className="pt-36 md:pt-40 pb-16 px-6 text-center">
                <div className="max-w-3xl mx-auto">
                    <p className="text-sm md:text-base font-extrabold text-primary-700 tracking-wide">{page.eyebrow}</p>
                    <h1 className="mt-3 text-4xl/normal md:text-5xl/normal font-extrabold text-gray-900 tracking-tight break-keep">
                        {page.title}
                    </h1>
                    <p className="mt-5 text-lg/relaxed md:text-xl/relaxed text-gray-600 font-medium break-keep text-balance">{page.intro}</p>
                </div>
            </section>

            {sections.map((s, i) => {
                const keys = s.partners.filter((p) => p.isKey);
                const rest = s.partners.filter((p) => !p.isKey);
                return (
                    <section
                        key={s.kind}
                        id={s.kind}
                        className={`scroll-mt-24 py-20 md:py-24 px-6 ${i % 2 === 0 ? 'bg-white border-y border-gray-100' : ''}`}
                    >
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center">
                                <p className="text-sm font-extrabold text-primary-700 tracking-wide">{s.eyebrow}</p>
                                <h2 className="mt-2 text-3xl/normal md:text-4xl/normal font-extrabold text-gray-900 tracking-tight break-keep">
                                    {s.title}
                                </h2>
                                <p className="mt-3 max-w-2xl mx-auto text-base/relaxed md:text-lg/relaxed text-gray-600 break-keep text-balance">{s.sub}</p>
                            </div>

                            <div className="mt-12 space-y-6">
                                {keys.map((p) => <KeyPartnerCard key={p.id} partner={p} />)}
                                {rest.length > 0 && (
                                    <div className={`grid gap-6 ${rest.length > 1 ? 'md:grid-cols-2' : ''}`}>
                                        {rest.map((p) => <PartnerCard key={p.id} partner={p} />)}
                                    </div>
                                )}
                            </div>

                            {s.note && (
                                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center">
                                    <p className="m-0 text-base text-gray-700 font-medium break-keep">{s.note}</p>
                                    <button
                                        type="button"
                                        onClick={openContact}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-solid border-gray-300 hover:border-primary-500 text-gray-800 hover:text-primary-700 text-sm font-bold px-5 py-2.5 transition-colors"
                                    >
                                        <MessagesSquare className="w-4 h-4" strokeWidth={2.25} aria-hidden="true" />
                                        연동 문의
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })}

            {/* 고객은 목록이 아니라 이 문장으로 — 적용사례가 그 이야기다. */}
            <section className="px-6 pt-16">
                <div className="max-w-5xl mx-auto rounded-3xl bg-primary-50 border border-primary-100 px-6 py-14 md:py-16 text-center">
                    <p className="m-0 text-2xl/normal md:text-3xl/normal font-extrabold text-gray-900 tracking-tight break-keep">
                        {page.closing}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/use-cases"
                            className="inline-flex items-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-base font-extrabold px-6 py-3 shadow-soft transition-colors"
                        >
                            적용사례 보기
                            <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                        </Link>
                        <button
                            type="button"
                            onClick={openContact}
                            className="inline-flex items-center gap-2 rounded-full bg-white border border-solid border-primary-200 hover:border-primary-500 text-primary-700 text-base font-extrabold px-6 py-3 transition-colors"
                        >
                            무료 상담 요청
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Partners;
